<template>
  <div class="analytics-chart">
    <div class="chart-header">
      <div class="chart-title">
        <h3>{{ title }}</h3>
      </div>
      <div class="chart-actions">
        <a-radio-group v-model="viewType" button-style="solid" @change="handleViewTypeChange">
          <a-radio-button value="chart">图形</a-radio-button>
          <a-radio-button value="table">表格</a-radio-button>
        </a-radio-group>
      </div>
    </div>

    <template v-if="hasData">
      <!-- 图表展示区域 -->
      <div v-show="viewType === 'chart'" ref="chartContainer" class="chart-container" />

      <!-- 表格展示区域 -->
      <div v-show="viewType === 'table'" class="table-container">
        <a-table :columns="columns" :data-source="tableData" :pagination="{ pageSize: 20 }" :scroll="{ x: '100%' }" size="middle" />
      </div>
    </template>
    <template v-else>
      <a-empty :description="emptyDescription" />
    </template>
  </div>
</template>

<script>
import { h } from 'vue';
import moment from 'moment';
import * as echarts from 'echarts';

export default {
  name: 'AnalyticsChart',
  props: {
    title: {
      type: String,
      default: '数据分析'
    },
    analysisData: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      chart: null,
      viewType: 'chart',
      timeSortDirection: 'desc',
      columns: [],
      tableData: []
    };
  },
  computed: {
    hasData() {
      return this.analysisData?.detailResult?.rows?.length > 0;
    },
    emptyDescription() {
      if (!this.analysisData) {
        return '请选择查询条件，点击查询按钮';
      }
      return '暂无数据';
    }
  },
  watch: {
    analysisData: {
      handler(val) {
        if (val?.detailResult) {
          // 销毁旧的图表实例
          if (this.chart) {
            this.chart.dispose();
            window.removeEventListener('resize', this.handleResize);
          }
          this.initChart();
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // this.initChart();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      window.removeEventListener('resize', this.handleResize);
    }
  },
  methods: {
    handleViewTypeChange() {
      this.$nextTick(() => {
        if (this.viewType === 'chart' && this.chart) {
          this.chart.resize();
        }
      });
    },
    initChart() {
      this.$nextTick(() => {
        this.chart = echarts.init(this.$refs.chartContainer);
        window.addEventListener('resize', this.handleResize);
        if (this.analysisData) {
          this.initData();
        }
      });
    },
    handleResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    initData() {
      console.log('initData');
      const { detailResult } = this.analysisData;

      // 初始化表格列
      this.initColumns(detailResult);

      // 初始化图表和表格数据
      this.updateChartAndTable(detailResult);
    },
    initColumns(detailResult) {
      // 基础指标列
      this.columns = [
        {
          title: '指标',
          dataIndex: 'metric',
          key: 'metric',
          width: 160
        },
        {
          title: '汇总',
          dataIndex: 'total',
          key: 'total',
          width: 160,
          align: 'center',
          sorter: (a, b) => a.total - b.total,
          render: text => text.toLocaleString()
        }
      ];

      // 添加时间列
      detailResult.series.forEach((time, index) => {
        this.columns.push({
          title: moment(time).format('MM-DD HH:mm'),
          dataIndex: `time_${index}`,
          key: `time_${index}`,
          align: 'center',
          width: 110,
          customRender: text => {
            if (text === 0) {
              return text.toLocaleString();
            }
            return h(
              'span',
              {
                style: {
                  color: '#1890ff',
                  backgroundColor: text > 0 ? '#e6f7ff' : 'transparent',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }
              },
              text.toLocaleString()
            );
          }
        });
      });
    },
    updateChartAndTable(detailResult) {
      const { series, rows } = detailResult;

      // 更新图表配置
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: function (params) {
            // 过滤掉数量为0的记录
            const filteredParams = params.filter(param => param.value !== 0);
            if (filteredParams.length === 0) return '';
            let result = `${moment(filteredParams[0].axisValue).format('YYYY-MM-DD HH:mm:ss')}<br/>`;
            filteredParams.forEach(param => {
              // 使用HTML实现数值右对齐
              result += `<div style="display:flex;justify-content:space-between;align-items:center;">
                <span>${param.marker} ${param.seriesName}</span>
                <span style="font-weight:bold;margin-left:12px;">${param.value.toLocaleString()}</span>
              </div>`;
            });
            return result;
          }
        },
        legend: {
          data: rows.map(row => row.by_values[0]),
          type: 'scroll',
          orient: 'vertical',
          right: 0,
          top: 10,
          bottom: 10,
          align: 'left',
          width: 200
        },
        grid: {
          left: '2%',
          right: 240,
          bottom: '2%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: series,
          axisLabel: {
            formatter: value => moment(value).format('MM-DD HH:mm')
          }
        },
        yAxis: {
          type: 'value'
        },
        series: rows.map(row => ({
          name: row.by_values[0],
          type: 'line',
          smooth: false, // 是否平滑折线图
          data: row.values,
          symbolSize: 6,
          emphasis: {
            focus: 'series'
          }
        }))
      };

      this.$nextTick(() => {
        this.chart.setOption(option, true); // 添加 true 参数以清除之前的配置
      });

      // 更新表格数据 - 行列转换并添加汇总
      this.tableData = rows.map(row => {
        const rowData = {
          key: row.by_values[0],
          metric: row.by_values[0],
          total: row.values.reduce((sum, curr) => sum + curr, 0) // 计算总和
        };

        // 为每个时间点添加对应的值
        row.values.forEach((value, index) => {
          rowData[`time_${index}`] = value;
        });

        return rowData;
      });

      // 默认按汇总降序排序
      this.tableData.sort((a, b) => b.total - a.total);
    },
    toggleTimeSort() {
      this.timeSortDirection = this.timeSortDirection === 'asc' ? 'desc' : 'asc';
      this.tableData.sort((a, b) => {
        return this.timeSortDirection === 'asc' ? a.total - b.total : b.total - a.total;
      });
    }
  }
};
</script>

<style lang="less" scoped>
.analytics-chart {
  background: #fff;
  border-radius: 2px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chart-title h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.chart-container {
  height: 400px;
  width: 100%;
}

.table-container {
  margin-top: 24px;
  overflow-x: auto;

  :deep(.ant-table-body) {
    overflow-x: auto !important;
  }

  :deep(.ant-table-fixed-left) {
    box-shadow: 6px 0 6px -4px rgba(0, 0, 0, 0.15);
  }
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
